import { faHandshakeSlash } from '@fortawesome/pro-solid-svg-icons';
import {
  StrategyConfig,
  StrategyValidationContext,
  CreateStrategyPayload,
  UpdateStrategyPayload,
} from '../types';
import {
  validateDynamicCriteria,
  hasUnfilledCriteriaPlaceholders,
} from '../../Admin/utils/strategyValidation';
import NotReferringConfiguration from '../../Admin/AddEditStrategy/components/StrategyTypes/NotReferringConfiguration';
import { transformCriteriaInputs } from './transformers';

/**
 * Configuration for Not Referring (Radius-Based Analysis) strategy type
 *
 * Understands a provider or site's "capture rate" in a given radius
 * around their primary address. Uses dynamic criteria templates plus radius.
 */
export const notReferringConfig: StrategyConfig = {
  type: 'NOT_REFERRING',
  label: 'Radius-Based Analysis',
  description:
    "Understand a provider or site's \"capture rate\" in a given radius around their primary address",
  icon: faHandshakeSlash,
  mode: 'engage',
  disabled: false,

  ConfigComponent: NotReferringConfiguration,

  requiredFields: ['label', 'description', 'criteria_template'],

  validateProgress: (context: StrategyValidationContext): boolean => {
    if (context.isEditMode || context.isCopyMode) {
      return !!(
        context.strategyDetails?.criteria_template ||
        context.strategyDetails?.codeSetId ||
        context.strategyDetails?.mode
      );
    }
    const template = context.dynamicCriteria?.criteria_template;
    // Check that template exists and all placeholders have valid data
    return !!template && !hasUnfilledCriteriaPlaceholders(context.dynamicCriteria);
  },

  validateComplete: (context: StrategyValidationContext): boolean => {
    if (context.isEditMode || context.isCopyMode) return true;
    return validateDynamicCriteria(context.dynamicCriteria);
  },

  serializeForCreate: (
    context: StrategyValidationContext
  ): CreateStrategyPayload => {
    return {
      label: context.formData.label,
      description: context.formData.description,
      type: 'NOT_REFERRING',
      max_opportunities: context.formData.max_opportunities,
      criteria_template: context.dynamicCriteria?.criteria_template || '',
      criteria_inputs: transformCriteriaInputs(
        context.dynamicCriteria?.criteria_inputs
      ),
      radius: context.notReferralsData?.radius ?? 0,
    };
  },

  serializeForUpdate: (
    context: StrategyValidationContext
  ): UpdateStrategyPayload => {
    return {
      label: context.formData.label,
      description: context.formData.description,
      max_opportunities: context.formData.max_opportunities,
      criteria_template: context.dynamicCriteria?.criteria_template || '',
      criteria_inputs: transformCriteriaInputs(
        context.dynamicCriteria?.criteria_inputs
      ),
      radius: context.notReferralsData?.radius ?? 0,
    };
  },

  features: {
    hasMaxOpportunities: true,
    usesRadius: true,
  },
};
